import {
    Table, TableBody, TableCell, TableContainer, TableHead, useTheme,
    TableRow, Paper, Typography, Box, TableSortLabel, TablePagination,
    Tooltip, IconButton, Menu, MenuItem, Checkbox, ListItemText
} from "@mui/material";
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import { LabDataItem } from "../Types/LabData";

interface LabDataTableProps {
    labData: LabDataItem[];
    handleToggleDensity: () => void;
    handleOpenColumnMenu: (event: React.MouseEvent<HTMLElement>) => void;
    handleCloseColumnMenu: () => void;
    handleToggleColumn: (columnId: string) => void;
    handleChangePage: (event: unknown, newPage: number) => void;
    responsiveColumns: any[];
    createSortHandler: (property: keyof LabDataItem) => () => void;
    paginatedData: LabDataItem[];
    isSmallScreen: boolean;
    handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
    density?: 'compact' | 'standard';
    columnMenuAnchor?: null | HTMLElement;
    columns?: any[];
    visibleColumns?: string[];
    page?: number;
    rowsPerPage?: number;
    orderBy?: keyof LabDataItem;
    order?: 'asc' | 'desc';
}

function LabDataTable({
    labData = [], handleToggleDensity, handleOpenColumnMenu, handleCloseColumnMenu,
    handleToggleColumn, handleChangePage, responsiveColumns, createSortHandler,
    paginatedData, isSmallScreen, handleChangeRowsPerPage, density = 'compact',
    columnMenuAnchor = null, columns = [], visibleColumns = [], page = 0,
    rowsPerPage = 10, orderBy = 'dateSampled' as keyof LabDataItem, order = 'desc'
}: LabDataTableProps) {
    const theme = useTheme();
    const mutedBlue = theme.palette.secondary.main;

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', mb: 4 }}>
            {labData.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        No lab data available
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                        <Tooltip title="Toggle table density">
                            <IconButton onClick={handleToggleDensity} size="small">
                                {density === 'compact' ? <DensitySmallIcon /> : <DensityMediumIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Show/hide columns">
                            <IconButton onClick={handleOpenColumnMenu} size="small">
                                <ViewColumnIcon />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={columnMenuAnchor}
                            open={Boolean(columnMenuAnchor)}
                            onClose={handleCloseColumnMenu}
                        >
                            {columns.map((column) => (
                                <MenuItem
                                    key={column.id}
                                    onClick={() => handleToggleColumn(column.id)}
                                    disabled={column.id === 'binName' || column.id === 'dateSampled'}
                                    dense
                                >
                                    <Checkbox
                                        checked={visibleColumns.includes(column.id)}
                                        size="small"
                                    />
                                    <ListItemText primary={column.label} />
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table
                            stickyHeader
                            aria-label="lab data table"
                            size={density === 'compact' ? "small" : "medium"}
                            sx={{
                                '& .MuiTableCell-root': {
                                    py: density === 'compact' ? 0.5 : 1,
                                    px: density === 'compact' ? 1 : 2,
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    {responsiveColumns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            style={{
                                                minWidth: column.minWidth,
                                                fontWeight: 'bold',
                                                padding: density === 'compact' ? '4px 8px' : '8px',
                                                color: mutedBlue,
                                            }}
                                            sortDirection={orderBy === column.id ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={createSortHandler(column.id as keyof LabDataItem)}
                                            >
                                                {column.tooltip ? (
                                                    <Tooltip title={column.tooltip}>
                                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                            {column.label}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                        {column.label}
                                                    </Typography>
                                                )}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedData.map((row, index) => (
                                    <TableRow hover tabIndex={-1} key={index}
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            height: density === 'compact' ? 36 : 'auto',
                                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(94, 129, 172, 0.08)' },
                                            '&:hover': {
                                                backgroundColor: 'rgba(94, 129, 172, 0.25)',
                                                transition: 'background-color 0.2s ease'
                                            }

                                        }}>
                                        {responsiveColumns.map((column) => {
                                            const value = row[column.id];
                                            return (
                                                <TableCell
                                                    key={column.id}
                                                    sx={{
                                                        padding: density === 'compact' ? '2px 8px' : '6px 8px',
                                                        fontSize: density === 'compact' ? '0.8125rem' : '0.875rem'
                                                    }}
                                                >
                                                    {column.id === 'dateSampled' && typeof value === 'string'
                                                        ? new Date(value).toLocaleDateString(undefined, {
                                                            month: '2-digit',
                                                            day: '2-digit',
                                                            year: '2-digit',
                                                            timeZone: 'UTC'
                                                        })
                                                        : value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={labData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                margin: 0
                            }
                        }}
                    />
                    {isSmallScreen && (
                        <Box px={2} pb={1}>
                            <Typography variant="caption" color="text.secondary">
                                * Scroll horizontally to view more data or rotate device
                            </Typography>
                        </Box>
                    )}
                </>
            )
            }
        </Paper >
    );
}

export default LabDataTable;